// Modules
import { useState, useRef, useEffect } from 'react';

// Functions
import { SocketInstance, SocketParticipants } from './functions/socket.instance';
import { DeviceFilterType, DeviceInstance } from './functions/devices.instance';

// Components
import { SectionComponent } from './components/sections/sections.component';
import { ResizeComponent } from './components/room/resize/resize.component';
import { CamerasComponent } from './components/room/cameras/cameras.component';
import { CreatorComponent } from './components/creator/creator.component';
import { ErrorComponent } from './components/error/error.component';
import { ParticipantsComponent } from './components/participants/participants.component';
import { CallComponent } from './components/call/call.component';
import { ForceComponent } from './components/force/force.component';

// Functions
import DetectRTC from './functions/computer.detect';

// Properties
import { DishProperties } from './dish.properties';

// Types
import { AvailableType, CallType, ConnectDeviceOptions, DishParticipants } from './dish.types';
import { ChatComponent } from './components/room/chat/chat.component';

// Styles
import './dish.styles.scss'
import 'react-toastify/dist/ReactToastify.css';
import { ChatMessageType } from './components/room/chat/chat.types';
import SelectorStreams from './components/selectorStreams/selectorStreams.component';
import { UserHelper } from '../../helpers/UserHelper';
import { EditProfileComponent } from './components/editProfile/editProfile.component';
import { TimeComponent } from './components/time/time.component';
import { UserInfoComponent } from './components/userInfo/userInfo.component';
import DishVideoRecording from './components/room/cameras/video/components/recording/dish.video.recording';
import { ShareFileComponent } from './components/shareFile/share-file.component';
import { DownloadFileComponent } from './components/downloadFile/download-file.component';
import Modal from '../modals/modal.component';
import useIdleTimer from '../../hooks/useIdleTimer';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { appEventEmitter } from '../../context/EventContext';
import { ConnectionStatusComponent } from '../connectionStatus/connectionStatus.component';
import { PtzCapabilities, PtzDeviceInfo } from './functions/socket.available';
import axios from 'axios';
import { environment } from '../../environment';
import { environmentConfig } from '../../environment';
import { useNetworkMode } from '../../hooks/useNetworkMode';
import { useElectron } from '../../hooks/useElectron';
import { useHasLocalAuth } from '../../hooks/useHasLocalAuth';
import { NetworkSwitch } from '../networkSwitch/NetworkSwitch';
import { ElectronGate } from '../electron';

// Assets
const boopSfx = require('./call.mp3');

/**
 * Dish Component
 * @description Sistema de videoconferencia
 * @param {DishProperties} properties
 * @returns {JSX.Element}
 */
export function DishComponent(properties: DishProperties): JSX.Element {
  const { t } = useTranslation();

  const { isElectron, host, network } = useElectron();

  const hasLocalAuth = useHasLocalAuth(properties.user);

  const { isOffline, toggleMode, isLoading } = useNetworkMode({ userId: properties.user.id });

  // random username
  const userName = Math.random().toString(36).substring(7);

  // socket Status:
  const [socketStatus, setSocketStatus] = useState<'disconnected' | 'connected' | 'error'>('disconnected');

  // reference of socket instance
  const socketRef = useRef<SocketInstance>();

  // users availables
  const [availables, setAvailables] = useState<AvailableType[]>([])

  // call to receive new rooms
  const [call, setCall] = useState<CallType>()

  // force close session
  const [force, setForce] = useState<boolean>(false)

  // devices
  const deviceRef = useRef<DeviceInstance>();

  // streams
  const [localScreams, setLocalScreams] = useState<MediaStream[]>([]);

  // participants
  const [remoteParticipants, setRemoteParticipants] = useState<DishParticipants>({});

  // showing help
  const [isShowingHelp, setIsShowingHelp] = useState<boolean>(false);

  // streaming Configuration
  const [audioOutput, setAudioOutput] = useState<string | undefined>()

  // client inactivity
  const [clientInactivity, setClientInactivity] = useState<string>('');

  // client screenshot permission
  const [clientScreenshotPermission, setClientScreenshotPermission] = useState<boolean>(false);

  // client PTZ permission
  const [clientPtzPermission, setClientPtzPermission] = useState<boolean>(false);

  // client N1 permission
  const [clientN1Permission, setClientN1Permission] = useState<boolean>(false);

  // Mindray credentials available in room
  const [mindrayCredentialsAvailable, setMindrayCredentialsAvailable] = useState<boolean>(false);

  // Mindray credentials list
  const [mindrayCredentials, setMindrayCredentials] = useState<Array<{
    uuid: string;
    n1_name_identifier: string | null;
    mindray_user: string;
    mindray_password: string;
  }>>([]);

  // chat messages
  const [messages, setMessages] = useState<ChatMessageType[]>([]);

  // last messages
  const [lastMessages, setLastMessages] = useState<ChatMessageType[]>([]);

  // layout selected
  const [layout, setLayout] = useState<string>('gallery');

  // room of videoconference
  const [room, setRoom] = useState<string | undefined>();

  // Track room where Mindray credentials were registered (for Electron clients)
  const mindrayRegisteredRoomRef = useRef<string | undefined>();

  // Idle time
  const isUserIdle = useIdleTimer(clientInactivity, room, Object.keys(remoteParticipants).length === 0);

  // permissions
  const [permissions, setPermissions] = useState<typeof DetectRTC>()

  // video status
  const [videoStatus, setVideoStatus] = useState<boolean>(true);

  // audio status
  const [audioStatus, setAudioStatus] = useState<boolean>(true);

  // local call
  const [localCall, setLocalCall] = useState<boolean>(false);

  // streams type map
  const [streamsTypeMap, setStreamsTypeMap] = useState<Map<string, string>>(new Map());

  // streams manual map
  const [streamsManualMap, setStreamsManualMap] = useState<Map<string, string>>(new Map());

  // user accepted call
  const [userAcepptedCall, setUserAcepptedCall] = useState<string>('');

  // user rejected call
  const [userRejectedCall, setUserRejectedCall] = useState<string>('');

  // can record
  const [canRecord, setCanRecord] = useState<boolean>(false);

  // devices loaded
  const [devicesLoaded, setDevicesLoaded] = useState<boolean>(false);

  // answered call
  const [answeredCall, setAnsweredCall] = useState<boolean>(false);

  // clicked users
  const [clickedUsers, setClickedUsers] = useState<{
    userId: string,
    socketId: string,
    username: string
  }[]>([]);

  // selected video
  const [selectedVideo, setSelectedVideo] = useState<boolean>(false);

  // is calling
  const [isCalling, setIsCalling] = useState<boolean>(false);

  const [showLogout, setShowLogout] = useState<boolean>(false);

  const [configOption, setConfigOption] = useState<string>('');

  const [estetoVolume, setEstetoVolume] = useState<number>(100);
  const [currentVolume, setCurrentVolume] = useState<number>(100);

  // Shared file
  const [username, setUsername] = useState<string>('');
  const [file, setFile] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const [fileUUID, setFileUUID] = useState<string>('');

  const [streamTrack, setStreamTrack] = useState<MediaStreamTrack | undefined>();

  const [concurrentCalls, setConcurrentCalls] = useState<CallType[]>([]);

  const [appsBeingUsed, setAppsBeingUsed] = useState<{ name: string, stream: MediaStream }[]>([]);

  const [isShutdown, setIsShutdown] = useState<boolean>(false);

  const [socketConnectionStatus, setSocketConnectionStatus] = useState<'excellent' | 'good' | 'moderate' | 'bad' | 'no-connection'>('good');

  const [isShowingConnectionStatus, setIsShowingConnectionStatus] = useState<boolean>(false);

  // Flag to prevent saving presets during restoration process
  const [isRestoringPresets, setIsRestoringPresets] = useState<boolean>(false);

  const checkSocketConnection = () => {
    if (!socketRef.current) {
      setSocketConnectionStatus('no-connection');
      return;
    }

    // Evaluate connection status
    if (socketRef.current.status === 'connected') {
      // Use the new quality assessment that considers both latency and jitter
      const quality = socketRef.current.getConnectionQuality();
      setSocketConnectionStatus(quality as 'excellent' | 'good' | 'moderate' | 'bad');
    } else {
      setSocketConnectionStatus('no-connection');
    }
  };

  // Añadir este useEffect después de los otros useEffect
  useEffect(() => {
    setTimeout(() => {
      checkSocketConnection();
    }, 1000);

    // Configurar un intervalo para verificar periódicamente
    const intervalId = setInterval(checkSocketConnection, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [socketRef.current]);

  const removeAppStream = (streamId: string) => {
    setAppsBeingUsed((prev) => prev.filter((app) => app.stream.id !== streamId));
  };

  /**
   * Save PTZ preset for a specific stream
   */
  const savePtzPreset = (streamId: string, position: { pan?: number, tilt?: number, zoom?: number }) => {
    // Don't save presets during restoration process
    if (isRestoringPresets) {
      return;
    }

    // Use simple persistent key for each user
    const persistentKey = `ptz-presets`;

    const savedPresetsStr = localStorage.getItem(persistentKey);

    let presets = [{
      id: 'initial',
      name: 'Estado Inicial',
      position: position
    }];

    if (savedPresetsStr) {
      try {
        const savedPresets = JSON.parse(savedPresetsStr);
        // Update the initial preset with new position
        const initialPresetIndex = savedPresets.findIndex((preset: any) => preset.id === 'initial');
        if (initialPresetIndex >= 0) {
          const updatedPosition = { ...savedPresets[initialPresetIndex].position, ...position };
          savedPresets[initialPresetIndex].position = updatedPosition;
          presets = savedPresets;
        }
      } catch (error) {
        console.error('❌ Error parsing saved PTZ presets for update:', error);
      }
    } else {
    }

    localStorage.setItem(persistentKey, JSON.stringify(presets));
  };  /**
   * Restore PTZ presets for all current streams when joining a room
   */
  const restorePtzPresets = () => {

    // Set flag to prevent automatic saving during restoration
    setIsRestoringPresets(true);

    // Restore presets for local streams using persistent key
    localScreams.forEach((stream) => {
      restorePtzPresetForStream(stream);
    });

    // Reset flag after a delay to allow restoration to complete
    setTimeout(() => {
      setIsRestoringPresets(false);
    }, 3000);
  };

  /**
   * Restore PTZ preset for a specific stream
   */
  const restorePtzPresetForStream = (stream: MediaStream) => {
    // Use persistent key: userId + roomId instead of changing streamId
    const persistentKey = `ptz-presets`;
    const savedPresetsStr = localStorage.getItem(persistentKey);

    if (!savedPresetsStr) {
      console.log(`❌ No saved presets found for user ${properties.user.id} in room ${room}`);
      return;
    }

    try {
      const savedPresets = JSON.parse(savedPresetsStr);
      const initialPreset = savedPresets.find((preset: any) => preset.id === 'initial');

      if (initialPreset && initialPreset.position) {

        // Apply the preset with a delay to ensure the stream is ready
        setTimeout(() => {
          applyPtzPreset(stream, initialPreset.position);
        }, 1000);
      } else {
        console.log(`❌ No initial preset found for user ${properties.user.id} in room ${room}`);
      }
    } catch (error) {
      console.error(`❌ Error parsing saved PTZ presets for restoration:`, error);
      // Clean up corrupted data
      localStorage.removeItem(persistentKey);
    }
  };

  /**
   * Apply PTZ preset to a MediaStream
   */
  const applyPtzPreset = (stream: MediaStream, position: any) => {

    const [track] = stream.getVideoTracks();
    if (!track || !track.applyConstraints || !track.getCapabilities) {
      console.warn(`❌ Cannot apply PTZ preset to stream ${stream.id}: no video track or capabilities`);
      return;
    }

    try {
      const capabilities = track.getCapabilities();

      const constraints: any = { advanced: [{}] };

      // @ts-ignore - PTZ capabilities are not in standard MediaTrackCapabilities type
      if (position.pan !== undefined && capabilities.pan) {
        // @ts-ignore
        const panValue = Math.max(capabilities.pan.min, Math.min(capabilities.pan.max, position.pan));
        constraints.advanced[0].pan = panValue;
      }

      // @ts-ignore - PTZ capabilities are not in standard MediaTrackCapabilities type
      if (position.tilt !== undefined && capabilities.tilt) {
        // @ts-ignore
        const tiltValue = Math.max(capabilities.tilt.min, Math.min(capabilities.tilt.max, position.tilt));
        constraints.advanced[0].tilt = tiltValue;
      }

      // @ts-ignore - PTZ capabilities are not in standard MediaTrackCapabilities type
      if (position.zoom !== undefined && capabilities.zoom) {
        // @ts-ignore
        const zoomValue = Math.max(capabilities.zoom.min, Math.min(capabilities.zoom.max, position.zoom));
        constraints.advanced[0].zoom = zoomValue;
      }

      // Apply the constraints
      track.applyConstraints(constraints)
        .then(() => {
        })
        .catch((error) => {
          console.error(`❌ Error restoring PTZ preset for ${stream.id}:`, error);
        });
    } catch (error) {
      console.error(`Error applying PTZ preset for ${stream.id}:`, error);
    }
  };

  const ringIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const callTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const ringTone = () => {
    if (isCalling && ringIntervalRef.current) {
      const audio = new Audio(boopSfx);
      audio.play();
    }
  }

  const updateUser = async () => {
    try {
      const response = await UserHelper.updateUserGroups();
      properties.setUser(response);
      localStorage.setItem('user', JSON.stringify(response));
    } catch (error) {
      console.error('Error updating user groups:', error);
    }
  }

  useEffect(() => {
    updateUser();
  }, []);

  useEffect(() => {
    socketRef.current?.emit('stream:change_volume', {
      volume: estetoVolume,
      room: room,
    });
  }, [estetoVolume]);

  useEffect(() => {
    if (clickedUsers.length > 0 && !isCalling) {
      setIsCalling(true);
    } else if (clickedUsers.length === 0 && isCalling) {
      setIsCalling(false);
    }
  }, [clickedUsers]);

  useEffect(() => {
    ringTone();

    ringIntervalRef.current = setInterval(() => {
      socketRef.current?.emit('call:is_still_active', {
        socketId: clickedUsers[0].socketId,
        username: clickedUsers[0].username
      });

      ringTone();
    }, 2000);

    if (!isCalling) {
      clearInterval(ringIntervalRef.current!);
    }

    return () => clearInterval(ringIntervalRef.current!);
  }, [isCalling]);

  useEffect(() => {
    // Clear any existing timeout when clickedUsers changes
    if (callTimeoutRef.current) {
      clearTimeout(callTimeoutRef.current);
      callTimeoutRef.current = null;
    }

    // If there are clicked users (active call attempts)
    if (clickedUsers.length > 0 && isCalling) {
      // Set timeout to auto-cancel call after 11 seconds
      callTimeoutRef.current = setTimeout(() => {
        // For each clicked user (normally just one)
        clickedUsers.forEach(clickedUser => {
          const available = availables.find(av => av.socketId === clickedUser.socketId);

          if (available) {
            // Find the corresponding available user
            toast(t('call.no-answer', {user: clickedUser.username}), {type: 'info'});

            // Remove user from clicked users
            setClickedUsers(prev => prev.filter(user => user.userId !== clickedUser.userId));

            // Cancel the call via socket
            socketRef.current?.emit('call:cancel', {
              available: available,
              room: room
            });

            // Stop ringing sound
            if (ringIntervalRef.current) {
              clearInterval(ringIntervalRef.current);
              ringIntervalRef.current = null;
            }

            setIsCalling(false);
          }
        });
      }, 21000); // 21 seconds timeout
    }

    // Cleanup when component unmounts
    return () => {
      if (callTimeoutRef.current) {
        clearTimeout(callTimeoutRef.current);
        callTimeoutRef.current = null;
      }
    };
  }, [clickedUsers, isCalling]);

  useEffect(() => {

    DetectRTC.load(function () {

      setPermissions(DetectRTC)
      DetectRTC.DetectLocalIPAddress((_ip: string) => {

      })

    });

  }, []);

  const removeMedStream = async (streamId: string) => {
    await appEventEmitter.emit('device:stopped', {
      action: 'stop',
      resource: 'device',
      agent: streamId,
      event_phase: 'end',
      user: properties.user,
    });
  }

  useEffect(() => {
    const interval = setInterval(() => {
      localScreams.forEach((stream, index) => {
        const type = streamsTypeMap.get(stream.id);

        if (!stream.active && type === 'med.device') {
          setLocalScreams((prev) => {
            prev.splice(index, 1);
            return [...prev];
          });

          removeMedStream(stream.id);

          socketRef.current?.removeLocalStream(index);
        }
      });
    }, 1000);

    return () => clearInterval(interval);

  }, [localScreams, room])

  useEffect(() => {
    // Get the localstreams that have their type as undefined
    const localStreamsWithTypeUndefined = localScreams.filter((stream) => !streamsTypeMap.has(stream.id));

    if (localStreamsWithTypeUndefined.length > 1) {
      localStreamsWithTypeUndefined.forEach((stream, index) => {
        if (!stream.active) {
          console.log(`The stream ${stream.id} is not active, proceeding to remove it`);
          // Remove the stream from the socket
          socketRef.current?.removeLocalStream(index);

          // Set the audiosta and videostatus to true
          setAudioStatus(true);
          setVideoStatus(true);
        }
      });
    }
  }, [localScreams]);

  const closeSession = () => {
    // disconnect socket
    socketRef.current?.disconnect()

    // remove participants
    socketRef.current?.removeParticipants();

    // reset scene
    properties.setScene('')

    // remove remote participants
    setRemoteParticipants({})

    // clean local streams
    setLocalScreams([])

    // delete user
    properties.setUser(undefined)

    // redirect to home to avoid video caché
    window.location.href = '/';

  }

  useEffect(() => {

    if (properties.scene === 'chat') {

      // reset the last messages
      setLastMessages([])

    }

  }, [properties.scene])

  useEffect(() => {
    if (concurrentCalls.length > 0) {
      setCall(concurrentCalls[0]);
    } else {
      setCall(undefined);
    }
  }, [concurrentCalls])

  const rejectCallOnLocal = (call: CallType) => {
    setConcurrentCalls((prev) => prev.filter((c) => c.available.user.id !== call.available.user.id));
  }

  const acceptCallOnLocal = (acceptedCall: CallType) => {
    setConcurrentCalls((prev) => {
      prev.forEach((concurrentCall) => {
        if (concurrentCall.available.user.id !== acceptedCall.available.user.id) {
          socketRef.current?.emit('call:rejected', {
            room: concurrentCall.room,
            available: concurrentCall.available
          });

          toast(`Llamada perdida de ${concurrentCall.available.user.name} ${concurrentCall.available.user.surnames}`, {
            type: 'error',
            autoClose: false,
          });
        }
      });

      return [];
    });
  }

  /**
   * Register Mindray credentials for a room (Electron only)
   * This is called when joining a room either by creating it or accepting a call
   */
  const registerMindrayCredentials = async (roomId: string) => {
    if (!isElectron) return;

    try {
      // Unregister from previous room if exists
      if (mindrayRegisteredRoomRef.current && mindrayRegisteredRoomRef.current !== roomId) {
        socketRef.current?.emit('mindray:unregister', {
          room: mindrayRegisteredRoomRef.current,
        });
        mindrayRegisteredRoomRef.current = undefined;
      }

      const gateway = await network?.getDefaultGateway();

      if (gateway && gateway.success && gateway.gateway) {
        const token = properties.user.access_token;

        try {
          const res = await axios.get(`${environment.api.host}/api/mindray/credentials/${gateway.gateway}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (res.status === 200) {
            const credentials = res.data;
            if (credentials && credentials.length > 0) {
              socketRef.current?.emit('mindray:register', {
                room: roomId,
                credentials,
              });
              mindrayRegisteredRoomRef.current = roomId;
            }
          }
        } catch (e) {
          console.error('Error fetching credentials for gateway', gateway, e);
        }
      }
    } catch (error) {
      console.error('Error registering Mindray credentials:', error);
    }
  };

  const createSocket = () => {
    // permissions
    DeviceInstance.getPermissions()

    // Create Instance of Devices
    const devices = new DeviceInstance();
    deviceRef.current = devices;

    // Create Instance of Socket
    const instance = new SocketInstance({
      debug: true,
      userId: properties.user.id,
      user: properties.user,
      roomId: room,
      userName: userName,
      localCall: localCall,
      askCloseSession: (ask) => {

        // show popup to close session
        setForce(ask)

      },
      closeSession: () => {
        closeSession()
      },
      onConnect: async () => setSocketStatus('connected'),
      onDisconnect: async () => {
        setSocketStatus('disconnected')

        await appEventEmitter.emit('disconnect:user', {
          action: 'disconnect',
          resource: 'user',
          agent: properties.user.id,
          event_phase: 'end',
          user: properties.user,
        });
      },
      onErrorConnecting: () => setSocketStatus('error'),
      onNewLocalStream: (stream: MediaStream, type?: {type: string}, manual?: {manual_id: string}) => setLocalScreams((prev) => {
        setStreamsTypeMap((prev) => {
          if (type) {
            prev.set(stream.id, type?.type);
          }

          return prev;
        });

        setStreamsManualMap((prev) => {
          if (manual) {
            prev.set(stream.id, manual.manual_id);
          }

          return prev;
        });

        const newStreams = [...prev, stream];

        // Restore PTZ presets for new streams after a delay to ensure they're ready
        setTimeout(() => {
          setIsRestoringPresets(true);
          restorePtzPresetForStream(stream);
          // Re-enable saving after restoration
          setTimeout(() => {
            setIsRestoringPresets(false);
          }, 2000);
        }, 2000);

        return newStreams;
      }),
      onRemoveLocalStream: (streamIndex: number) => {
        setLocalScreams((prev) => {
          prev.splice(streamIndex, 1)
          return [...prev]

        })
      },
      onMessage: async (message: ChatMessageType) => {
        setMessages((prev) => [...prev, message])
        if (properties.scene === 'chat') setLastMessages([])
        else setLastMessages((prev) => [...prev, message])

        if (message.type !== 'me') {
          await appEventEmitter.emit(`message:received`, {
            action: 'receive',
            resource: 'message',
            agent: message.room,
            event_phase: 'received',
            room: message.room,
            sender: message.name,
            user: properties.user,
          });
        }
      },
      onAvailables: (availables: AvailableType[]) => {
        setAvailables(availables)
      },
      onRoom: async (room: string) => {
        setRoom(room)
        socketRef.current?.setRoom(room)
        instance.removeParticipants();
        instance.joinRoom(room);
        setAppsBeingUsed([]);

        // Register Mindray credentials for Electron clients
        // @ts-ignore
        if (window.electron && window.electron.getDefaultGateway) {
          registerMindrayCredentials(room);
        }
      },

      onCall: (call: CallType) => {
        setConcurrentCalls((prev) => [...prev, call]);
      },
      onCanceled: (canceled: CallType) => {
        toast(`Llamada perdida de ${canceled.available.user.name} ${canceled.available.user.surnames}`, {
          type: 'error',
          autoClose: false
        });

        setConcurrentCalls((prev) => prev.filter((c) => c.available.user.id !== canceled.available.user.id));
      },
      reconnectToRoom: (room: string) => {
        // functionality removed by client
      },
      onRejected: async (rejected: CallType) => {
        setUserRejectedCall(rejected.available.user.id);
        setClickedUsers((prev) => prev.filter((user) => user.socketId !== rejected.available.socketId));
        setIsCalling(false);

        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }
      },
      onAccepted: async (accepted: CallType) => {
        setUserAcepptedCall(accepted.available.user.id);
        setClickedUsers((prev) => prev.filter((user) => user.socketId !== accepted.available.socketId));
        setIsCalling(false);

        if (callTimeoutRef.current) {
          clearTimeout(callTimeoutRef.current);
          callTimeoutRef.current = null;
        }
      },

      onUpdateParticipants: (newParticipants: SocketParticipants): void => {
        // clone participants
        const cloneParticipants: DishParticipants = {};

        // add new participants
        newParticipants && Object.keys(newParticipants) && Object.keys(newParticipants)?.map((id) => {

          cloneParticipants[id] = {
            id: id,
            user: newParticipants[id].user,
            streams: newParticipants[id]?.streams
          };

        });

        // update participants
        setRemoteParticipants(cloneParticipants);

      },
      onRemoveStreamParticipant: (streamId: string) => {

        // remove stream of participant
        setRemoteParticipants((prev) => {

          // search stream of participant and remove
          Object.keys(prev).map((id) => {

            prev[id].streams = prev[id].streams.filter((stream) => stream.id !== streamId);

          });

          return { ...prev }

        })

      },

      onChangeLayout: (layout: string) => {
        setLayout(layout);
      },

      onRemoveStream: (data: any) => {
        removeAppStream(data.streamId);
        setRemoteParticipants((prev) => {
            Object.keys(prev).map((id) => {

              prev[id].streams = prev[id].streams.filter((stream) => stream.id !== data.streamId);

            });

            return { ...prev }
         })

         setSelectedVideo(false);
      },

      onChangeStreamStatus: async (streamId: string, status: boolean, type: string) => {
        (type === 'video' || type === 'audio') &&
        await appEventEmitter.emit(`stream:${type}:toggled`, {
          action: 'toggle',
          resource: 'stream',
          agent: streamId,
          event_phase: 'start',
          enabled: status,
          user: properties.user,
        });

        setRemoteParticipants((prev) => {

          Object.keys(prev).map((id) => {

            prev[id].streams = prev[id].streams.map((stream) => {

              if (stream.id === streamId) {

                switch (type) {
                  case 'audio':
                    stream.getAudioTracks().map((track) => track.enabled = status);
                    break;
                  case 'video':
                    stream.getVideoTracks().map((track) => track.enabled = status);
                    break;
                }
              }

              return stream;

            });

          });

          return { ...prev }

        });
      },
      onFileShare: (username: string, file: string, filename: string, fileUUID: string) => {
        properties.setScene('file-shared');
        setUsername(username);
        setFile(file);
        setFilename(filename);
        setFileUUID(fileUUID);
      },

      onChangeStreamTrackStatus: (streamId: string, trackId: string, status: boolean, type: 'audio' | 'video', roomId?: string, streamType?: string, manual?: string) => {
        // Función para actualizar los participantes
        const updateParticipants = async (prev: DishParticipants) => {
          // Clonamos el objeto prev para evitar mutaciones directas del estado
          const newParticipants = { ...prev };

          // Función para esperar a que los streams se actualicen
          const waitForStreams = (participant: any) => {

            return new Promise((resolve, reject) => {

              let tries = 0;
              const maxTries = 10;

              // Función para verificar si los streams se han actualizado
              const checkStreams = () => {

                // Si el participante no tiene streams, esperar 1000ms antes de volver a verificar
                if (participant.streams.length === 0) {

                  setTimeout(checkStreams, 1000);

                  tries++;

                  if (tries >= maxTries) {
                    reject('No streams found');
                  }

                } else { // Si el participante tiene streams, actualizar los tracks
                  participant.streams.forEach((stream: MediaStream) => {
                    if (stream.id === streamId) {
                      setStreamsTypeMap((prev) => {
                        streamType && prev.set(stream.id, streamType);
                        return prev;
                      });

                      if (manual) {
                        setStreamsManualMap((prev) => {
                          prev.set(stream.id, manual);
                          return prev;
                        });
                      }

                      switch (type) {
                        case 'audio':
                          stream.getAudioTracks().forEach((track) => {
                            if (track.id === trackId) {
                              track.enabled = status;
                            }
                          });
                          break;
                        case 'video':
                          stream.getVideoTracks().forEach((track) => {
                            if (track.id === trackId) {
                              track.enabled = status;
                            }
                          });
                          break;
                      }
                    } else if (streamType === 'share.screen' || streamType === 'med.device') {
                      setStreamsTypeMap((prev) => {
                        prev.set(streamId, streamType);
                        return prev;
                      });

                      if (manual) {
                        setStreamsManualMap((prev) => {
                          prev.set(streamId, manual);
                          return prev;
                        });
                      }
                    }
                  });

                  resolve(true); // Resolvemos la promesa
                }
              };

              // Iniciar la verificación de los streams
              checkStreams();
            });
          };

          // Si no hay participantes, devolver el objeto vacío
          if (Object.keys(newParticipants).length === 0) {
            return newParticipants;
          }

          // Esperar a que los streams de cada participante se actualicen
          await Promise.all(Object.keys(newParticipants).map((id) => waitForStreams(newParticipants[id])));

          // Devolver el objeto actualizado
          return newParticipants;
        };

        setRemoteParticipants((prev) => {

          updateParticipants(prev)
            .then((newParticipants) => setRemoteParticipants(newParticipants));

          return prev; // Retornar el estado anterior mientras se actualiza
        });
      },

      onChangeVolume: (volume: number) => {
        setCurrentVolume(volume);
      },

      onUpdateLocalStream: (newStream: MediaStreamTrack) => {
        setStreamTrack(newStream);
      },

      onNoLongerActive: (socketId: string, username: string) => {
        setClickedUsers((prev) => prev.filter((user) => user.socketId !== socketId));
        setIsCalling(false);

        if (ringIntervalRef.current) {
          clearInterval(ringIntervalRef.current);
          ringIntervalRef.current = null;
        }

        const userLang = localStorage.getItem('language');
        switch (userLang) {
          case 'fr':
            toast(`${username} n'est plus disponible`, { type: 'error' });
            break;
          case 'en':
            toast(`${username} is no longer available`, { type: 'error' });
            break;
          case 'pt':
            toast(`${username} não está mais disponível`, { type: 'error' });
            break;
          default:
            toast(`${username} ya no se encuentra disponible`, { type: 'error'});
            break;
        }

      },

      onPtzDevicesListReceived: (devices: PtzDeviceInfo[]) => {
        console.log('PTZ Devices List Received:', devices);
        setPtzDevices(prevDevices => {
          return [...devices];
        });
      },

      onPtzCommandMove: (deviceId: string, command: string, value: number) => {
        console.log('PTZ Command Move received:', { deviceId, command, value });
        // Llamar a la función que maneja los comandos PTZ
        handlePtzCommandReceived(deviceId, command, value);
      },

      onPtzCapabilitiesReceived: (deviceId: string, capabilities: PtzCapabilities) => {
        console.log('PTZ Capabilities received:', { deviceId, capabilities });

        setPtzDevices(prevDevices => {
          console.log('PTZ Devices before update:', prevDevices);

          const deviceToUpdate = prevDevices.find(device => device.id === deviceId);

          if (deviceToUpdate) {
            console.log(`Updating capabilities for device ${deviceId}`);
            return prevDevices.map(device =>
              device.id === deviceId ? { ...device, capabilities } : device
            );
          } else {
            console.warn(`PTZ Device with ID ${deviceId} not found for capabilities update.`);
            // Si el dispositivo no existe, lo agregamos con las capacidades recibidas
            console.log(`Adding new PTZ device ${deviceId} with capabilities`);
            return [...prevDevices, { id: deviceId, capabilities } as PtzDeviceInfo];
          }
        });
      },

      onMindrayCredentialsAvailable: (data) => {
        console.log('Mindray credentials available:', data);
        setMindrayCredentialsAvailable(data.available);
      },

      onMindrayCredentialsList: (data) => {
        console.log('Mindray credentials list:', data);
        if (data.success) {
          setMindrayCredentials(data.credentials);
        }
      },
    });

    socketRef.current = instance;

  }

  const [ptzDevices, setPtzDevices] = useState<PtzDeviceInfo[]>([]);

  // Función para manejar comandos PTZ recibidos
  const handlePtzCommandReceived = (deviceId: string, command: string, value: number) => {
    console.log('PTZ Command received in main component:', { deviceId, command, value });

    // Buscar el handler en la referencia global
    const handlers = (window as any).ptzHandlers || {};
    if (handlers[deviceId]) {
      handlers[deviceId](deviceId, command, value);
    } else {
      console.warn(`No PTZ handler found for device ${deviceId}`);
    }
  };  // socket reset function
  const removeSocket = () => {
    // Unregister Mindray credentials if in Electron
    // @ts-ignore
    if (window.electron?.getLocalIP && room) {
      socketRef.current?.emit('mindray:unregister', { room });
    }

    // remove room
    socketRef.current?.setRoom(undefined)

    // disconnect socket
    socketRef.current?.disconnect();

    // remove socket instance
    socketRef.current = undefined;

    // remove device instance
    deviceRef.current = undefined;

    // clean all data
    setLocalScreams([]);
    setRemoteParticipants({});
    setMessages([]);
    setSocketStatus('disconnected');
    setRoom(undefined);
    setAvailables([]);
    setCall(undefined);
    setLayout('default');
    setAudioOutput(undefined);
    setMindrayCredentialsAvailable(false);
    setMindrayCredentials([]);

  }

  useEffect(() => {
    // create socket in first render
    createSocket()

    UserHelper.updateUserLastLogin(properties.user)
      .then(() => {
        console.log(`[Login]: Login at ${new Date().toISOString()}`)
      }).catch((error) => {
          console.error('Error updating last login', error)
      });

    return () => {
      // remove socket in unmount
      removeSocket()
    }

  }, []);

  useEffect(() => {

    if (properties.room) {

      // reset the messages
      setMessages([])

      // reset the last messages
      setLastMessages([])

      // delete the streams
      localScreams.forEach((stream) => {
        socketRef.current?.emit('stream:remove_remote', {
          room: room,
          streamId: stream.id,
        });
      })

      // reset the availables
      setAvailables([]);

      // reset the remote participants
      setRemoteParticipants({})

      // remove participants of socket instance
      socketRef.current?.removeParticipants()

      // join to room
      socketRef.current?.emit('room:create', {
        room: properties.room,
        user: properties.user
      })

      // Restore PTZ presets after joining room (with delay to allow streams to stabilize)
      setTimeout(() => {
        restorePtzPresets();
      }, 2000);
    }

  }, [properties.room])

  const handleToggleNetworkMode = () => {
    if (isLoading) return;
    toggleMode();
  }

  // permissions error
  if (!permissions) return <></>

  return <div
    className={`DishComponent ${properties.scene === 'selector-streams' ? 'bottom' : properties.scene === 'participants' ? 'le0ft' : ''}`}
  >
    <div className={`header ${isShowingHelp ? 'help' : ''}`}>
      <div className='version'>
        <div className='logos-container'>
          <img
          src='/images/logo_sippar_h_v2.webp'
          alt='logo'
          className='Logotype sippar-logo'
        />
        {environmentConfig.shouldShowMinDefLogos() && (
          <img
            src='/images/logo_minisdef.svg'
            alt='logo_minisdef'
            className='Logotype minisdef-logo'
          />
        )}
        </div>
      </div>
      <TimeComponent />
      <div className="info">
        {
          canRecord && room && <DishVideoRecording layout={layout} />
        }

        <UserInfoComponent
          name={properties.user?.name || ''}
          isGeneric={properties.user?.is_generic}
          hasLocalAuth={hasLocalAuth}
          setShowLogout={setShowLogout}
          setConfigOption={setConfigOption}
          setScene={properties.setScene}
        />

        {/* Online/Offline toggle switch */}
        <ElectronGate>
          <NetworkSwitch
            isOffline={isOffline}
            isLoading={isLoading}
            onToggle={handleToggleNetworkMode}
          />
        </ElectronGate>
        <div
          className={`connection-indicator ${socketConnectionStatus}`}
          onClick={() => {
            setIsShowingConnectionStatus(!isShowingConnectionStatus);
          }}
        >
          <div className="signal-icon">
            <div className="signal-bar bar1"></div>
            <div className="signal-bar bar2"></div>
            <div className="signal-bar bar3"></div>
            <div className="signal-bar bar4"></div>
          </div>
        </div>
        <i className='las la-question-circle' style={{
            fontSize: '32px',
            color: '#1b3f7c'
        }}
        onClick={
          () => {
            properties.setScene('help')
          }
        }
        ></i>
      </div>
    </div>

    {
      room === undefined && socketRef.current && socketRef.current.status === 'connected' && deviceRef.current && <CreatorComponent
        setRoom={properties.setRoom}
        setScene={properties.setScene}
        setLocalCall={setLocalCall}
        localCall={localCall}
        user={properties.user}
        userSettings={properties.userSettings}
        sockerInstance={socketRef.current}
        availables={availables}
        localStreams={localScreams}
        setLocalStreams={(streams) => setLocalScreams(streams)}
        deviceInstance={deviceRef.current}
        setVideoStatus={(status) => {
          setVideoStatus(status)
          localStorage.setItem('video_status', status.toString())
        }}
        setAudioStatus={(status) => {
          setAudioStatus(status)
          localStorage.setItem('audio_status', status.toString())
        }}
        devicesLoaded={devicesLoaded}
        setDevicesLoaded={setDevicesLoaded}
      />
    }
    {
      socketStatus === 'error' && <ErrorComponent removeSocketConn={removeSocket}/>
    }

    {socketRef.current && deviceRef.current && <SectionComponent
      showLogout={showLogout}
      setShowLogout={setShowLogout}
      setClientInactivity={setClientInactivity}
      setClientScreenshotPermission={setClientScreenshotPermission}
      setClientPtzPermission={setClientPtzPermission}
      setClientN1Permission={setClientN1Permission}
      clientN1Permission={clientN1Permission}
      mindrayCredentialsAvailable={mindrayCredentialsAvailable}
      mindrayCredentials={mindrayCredentials}
      requestMindrayCredentials={() => {
        if (room) {
          socketRef.current?.emit('mindray:get_credentials', { room });
        }
      }}
      room={room}
      setScene={properties.setScene}
      participants={remoteParticipants}
      numberParticipants={Object.keys(remoteParticipants).length}
      availables={[]}
      setUser={properties.setUser}
      lastMessages={lastMessages}
      setLastMessages={(messages) => setLastMessages(messages)}
      scene={properties.scene}
      setLocalCall={setLocalCall}
      localCall={localCall}
      socket={socketRef.current}
      user={properties.user}
      userSettings={properties.userSettings}
      videoStatus={videoStatus}
      audioStatus={audioStatus}
      setVideoStatus={(status: boolean) => {
          setVideoStatus(status)
          localStorage.setItem('video_status', status.toString())
        }
      }
      setAudioStatus={(status: boolean) => {
          setAudioStatus(status)
          localStorage.setItem('audio_status', status.toString())
        }
      }
      layout={layout}
      setAudioOutputDevice={(deviceId) => setAudioOutput(deviceId)}
      audioOutputDevice={audioOutput}
      messages={messages}
      setLayout={(layout) => {
        setLayout(layout)
      }}
      socketStatus={socketStatus}
      localScreams={localScreams}
      getAudioOutputDevices={async () => await deviceRef.current?.getAudioOutput()}
      getDevices={async (filter?: DeviceFilterType) => await deviceRef.current?.getDevices(filter)}
      setIsShowingHelp={setIsShowingHelp}
      isShowingHelp={isShowingHelp}

      connectDevice={async (devices: { videoId: string } | { audioId: string } | { videoId: string, audioId: string }, options: ConnectDeviceOptions) => {

        const stream = await deviceRef.current?.getDeviceMedia(devices, {
          ...options
        });
        stream && socketRef.current?.addLocalStream(stream, {type: 'media'});

        return stream;

      }}
      connectStream={(stream: MediaStream) => {

        stream && socketRef.current?.addLocalStream(stream, {type: 'media'});

        return stream;

      }}
      sendMessage={(message: string) => socketRef?.current?.sendMessage(message)}
      getStreenMedia={async () => {

        const screenMedia = await deviceRef.current?.getScreenMedias();
        screenMedia && socketRef.current?.addLocalStream(screenMedia, {type: 'screen'});



      }}

      finishCall={async () => {
        if (localScreams) {
          console.log('PTZ Devices before disconnect:', ptzDevices);
          console.log('Local Screams before disconnect:', localScreams);

          // Search in the PTZ Devices
          const ptzDevice = ptzDevices.find(device => device.id === localScreams[0].id);

          console.log('PTZ Device found:', ptzDevice);

          if (ptzDevice && socketRef.current) {
            console.log('Disconnecting PTZ Device:', ptzDevice.id);
            console.log('Is Socket Connected:', socketRef.current);

            // Emit the event to socket with required fields
            socketRef.current.emit('ptz:disconnect', {
              deviceId: ptzDevice.id,
              reason: 'manual' as const,
              ownerId: properties.user.id,
            });

            // Wait a bit to ensure the event is sent before disconnecting
            await new Promise(resolve => setTimeout(resolve, 100));
          }

          localScreams.map((stream) => stream.getTracks().map((track) => track.stop()));
        }

        removeSocket()

        properties.setScene();
        socketRef.current?.setRoom(undefined)
        properties.room && properties.setRoom();
        createSocket()

      }}
      deviceInstance={deviceRef.current}
      addStream={(stream, type) => {
        socketRef.current?.addLocalStream(stream, type)
      }}
      removeShareScreen={(stream) => {
        socketRef.current?.removeShareScreen(stream)
      }}
      answeredCall={answeredCall}
      setAnsweredCall={setAnsweredCall}
      clickedUsers={clickedUsers}
      setClikedUsers={setClickedUsers}
      setIsShutdown={setIsShutdown}
    />}
    {
      room && socketRef.current && properties.scene === 'participants' && <ParticipantsComponent
        setScene={properties.setScene}
        availables={availables}
        socket={socketRef.current}
        room={room}
        user={properties.user}
        participants={remoteParticipants}
        rejectedCall={userRejectedCall}
        setUserRejectedCall={setUserRejectedCall}
        acceptedCall={userAcepptedCall}
        setUserAcceptedCall={setUserAcepptedCall}
        clickedUsers={clickedUsers}
        setClickedUser={setClickedUsers}
        isCalling={isCalling}
        setIsCalling={setIsCalling}
      />
    }
    {
      room && socketRef.current && properties.scene === 'file' && <ShareFileComponent
        room={room}
        setScene={properties.setScene}
        user={properties.user}
        participants={remoteParticipants}
        socket={socketRef.current}
      />
    }
    {
      socketRef.current && properties.scene === 'edit-profile' &&
      <EditProfileComponent
        setScene={properties.setScene}
        userInfo={properties.user}
        setUser={properties.setUser}
        configOption={configOption}
      />
    }
    {
      room && socketRef.current && properties.scene === 'chat' && <ChatComponent
        setSection={() => {
          setLastMessages([])
          properties.setScene()
        }}
        messages={messages}
        sendMessage={async (message: string) => {
          socketRef?.current?.sendMessage(message)

          await appEventEmitter.emit('message:sent', {
            action: 'send',
            resource: 'message',
            agent: room || '',
            event_phase: 'sent',
            room: room,
            user: properties.user,
          });
        }}
        room={room}
      />
    }
    {
      socketRef.current && room && deviceRef.current && <CamerasComponent
        userSettings={properties.userSettings}
        clientScreenshotPermission={clientScreenshotPermission}
        clientPtzPermission={clientPtzPermission}
        streamsTypeMap={streamsTypeMap}
        streamsManualMap={streamsManualMap}
        socketInstance={socketRef.current}
        localScreams={localScreams}
        remoteParticipants={remoteParticipants}
        deviceInstance={deviceRef.current}
        layout={layout}
        videoStatus={videoStatus}
        audioStatus={audioStatus}
        room={room}
        setAudioStatus={(status) => { setAudioStatus(status) }}
        setVideoStatus={(status) => { setVideoStatus(status) }}
        canRecord={canRecord}
        setCanRecord={setCanRecord}
        selectedVideo={selectedVideo}
        setSelectedVideo={setSelectedVideo}
        estetoVolume={currentVolume}
        setEstetoVolume={setEstetoVolume}
        streamTrack={streamTrack}
        setIsShowingHelp={setIsShowingHelp}
        ptzDevices={ptzDevices}
        onPtzCommandReceived={handlePtzCommandReceived}
        savePtzPreset={savePtzPreset}
      />
    }
    {
      socketRef.current && force && <ForceComponent
        socket={socketRef.current}
        setCall={setCall}
        setForce={setForce}
        closeSession={() => {
          setForce(false)
          closeSession();
        }}
        setRoom={(newRoom) => {
          setMessages([])
          setLastMessages([])
          // eliminamos los streams
          localScreams.forEach((stream) => {
            socketRef.current?.emit('stream:remove_remote', {
              room: room,
              streamId: stream.id,
            });
          })
          setAvailables([]);
          setRemoteParticipants({})
          socketRef.current?.removeParticipants()
          socketRef.current?.setRoom(newRoom)
          setRoom(newRoom)
          if (properties.room) {
            properties.setRoom(undefined)
          }
        }}
        user={properties.user}
      />
    }
    {
      socketRef.current && call && <CallComponent
        key={`${call.available.user.id}-${call.room}`}
        rejectCall={rejectCallOnLocal}
        acceptCall={acceptCallOnLocal}
        socket={socketRef.current}
        setCall={setCall} call={call}
        setRoom={(newRoom) => {
          setMessages([])
          setLastMessages([])
          // eliminamos los streams
          localScreams.forEach((stream) => {
            socketRef.current?.emit('stream:remove_remote', {
              room: room,
              streamId: stream.id,
            });
          })
          setAvailables([]);
          setRemoteParticipants({})
          socketRef.current?.removeParticipants()
          setRoom(newRoom)
          socketRef.current?.setRoom(newRoom)
          if (properties.room) {
            properties.setRoom(undefined)
          }
        }}
        onRoomJoined={registerMindrayCredentials}
        setAnsweredCall={setAnsweredCall}
        user={properties.user}
        devicesLoaded={devicesLoaded}
      />
    }
    {
      isShowingConnectionStatus && <ConnectionStatusComponent
        setIsShowingConnectionStatus={setIsShowingConnectionStatus}
        socketConnectionStatus={socketConnectionStatus}
        latency={socketRef.current?.latency || 0}
        jitter={socketRef.current?.jitter || 0}
        setScene={properties.setScene}
        room={room}
        participantsLength={Object.keys(remoteParticipants).length}
      />
    }
    {
      deviceRef.current && socketRef.current &&
      (
        properties.scene === 'selector-streams'
      )

      && <SelectorStreams
        mode='media'
        localScreams={localScreams}
        addStream={(stream, type, manual) => socketRef.current?.addLocalStream(stream, type || {type: 'media'}, manual || {manual_id: ''})}
        removeStream={(streamIndex: number) => socketRef.current?.removeLocalStream(streamIndex)}
        deviceInstance={deviceRef.current}
        setScene={properties.setScene}
        user={properties.user}
        appsBeingUsed={appsBeingUsed}
        setAppsBeingUsed={setAppsBeingUsed}
        setIsShowingHelp={setIsShowingHelp}
        setDevicesIconCache={properties.setDevicesIconCache}
        setMediaIconCache={properties.setMediaIconCache}
        devicesIconCache={properties.devicesIconCache}
        mediaIconCache={properties.mediaIconCache}
        room={room}
      />
    }
    {
      room && socketRef.current && properties.scene === 'file-shared'
      && <DownloadFileComponent
        setScene={properties.setScene}
        username={username}
        file={file}
        filename={filename}
        room={room}
        fileUUID={fileUUID}
        user={properties.user}
      />
    }
    {
      isUserIdle && <Modal
        type='warning'
        isOpen={isUserIdle}
        onClose={() => closeSession()}
        body={
          {
            title: `${t('modal.inactivity.title')}`,
            content: `${t('modal.inactivity.description')}`
          }
        }
        buttons={
          [
            {
              text: `${t('modal.inactivity.button')}`,
              class: 'close',
              onClick: () => closeSession()
            }
          ]
        }
      />
    }
    {
      isShutdown && <Modal
        type='warning'
        isOpen={isShutdown}
        onClose={() => setIsShutdown(false)}
        body={
          {
            title: `${t('modal.shutdown.title')}`,
            content: `${t('modal.shutdown.description')}`
          }
        }
        buttons={
          [
            {
              text: `${t('modal.shutdown.cancel')}`,
              class: 'cancel',
              onClick: () => setIsShutdown(false)
            },
            {
              text: `${t('modal.shutdown.confirm')}`,
              class: 'confirm',
              onClick: () => {
                host?.shutdown();
              }
            }
          ]
        }
      />
    }
    <ResizeComponent key={`${properties.scene}-${Object.keys(remoteParticipants).length}-${localScreams.length}`} />
  </div>

}
